import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { metricsApi } from "@/api/metrics";
import { parametersApi } from "@/api/parameters";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { fmt as fmtCanonical, num as numCanonical, rangeDaysBefore, todayIso } from "@/lib/format";

const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number, digits = 0): string => fmtCanonical(n, digits);

export function StatisticsPage() {
  const { t } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const [days, setDays] = useState(30);

  const { from, to } = useMemo(() => rangeDaysBefore(end, days), [end, days]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });

  const kgu = useQuery({
    queryKey: ["kgu-params", end],
    queryFn: () => parametersApi.getKguParams(end),
  });

  const stats = useMemo(() => {
    const rows = daily.data ?? [];
    let totalKwh = 0;
    let totalProfit = 0;
    let totalRevenue = 0;
    let totalHeat = 0;
    let balanceViolations = 0;
    let alarmHours = 0;
    for (const r of rows) {
      totalKwh += num(r.fact_generation_kwh);
      totalProfit += num(r.fact_profit_uah);
      totalRevenue += num(r.fact_revenue_uah);
      totalHeat += num(r.fact_heat_gcal);
      balanceViolations += r.balance_violations;
      alarmHours += r.alarm_hours;
    }

    const dayCount = rows.length || 1;
    const hoursTotal = dayCount * 24;
    const avgPower = totalKwh / hoursTotal; // average kW
    // KIUM = average / nominal max
    const pmax = num(kgu.data?.power_max_kw);
    const kium = pmax > 0 ? (avgPower / pmax) * 100 : 0;
    const profitPerMwh = totalKwh > 0 ? totalProfit / (totalKwh / 1000) : 0;
    const revenuePerMwh = totalKwh > 0 ? totalRevenue / (totalKwh / 1000) : 0;

    return {
      totalKwh,
      totalProfit,
      totalHeat,
      avgPower,
      kium,
      profitPerMwh,
      revenuePerMwh,
      balanceViolations,
      alarmHours,
      dayCount,
    };
  }, [daily.data, kgu.data]);

  const chartData = useMemo(
    () =>
      (daily.data ?? []).map((r) => {
        const gen = num(r.fact_generation_kwh);
        const profit = num(r.fact_profit_uah);
        return {
          day: r.day.slice(5),
          avg_power_kw: gen / 24,
          profit_per_mwh: gen > 0 ? profit / (gen / 1000) : 0,
        };
      }),
    [daily.data],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("statistics.title")}</CardTitle>
          <CardDescription>{t("statistics.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>{t("statistics.range_days")}</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
              className="w-[120px]"
            />
          </div>
          <div className="space-y-1">
            <Label>{t("finance.end_date")}</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-[180px]"
            />
          </div>
          <div className="ml-auto text-xs text-slate-500 self-end">{from} … {to}</div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t("statistics.kium")} value={`${fmt(stats.kium, 1)}%`} />
        <Stat
          label={t("statistics.avg_power")}
          value={`${fmt(stats.avgPower, 0)} ${t("common.kw")}`}
        />
        <Stat label={t("statistics.profit_per_mwh")} value={fmt(stats.profitPerMwh, 0)} />
        <Stat label={t("statistics.revenue_per_mwh")} value={fmt(stats.revenuePerMwh, 0)} />
        <Stat
          label={t("statistics.total_kwh")}
          value={`${fmt(stats.totalKwh / 1000, 1)} ${t("statistics.unit_mwh")}`}
        />
        <Stat
          label={t("statistics.total_heat")}
          value={`${fmt(stats.totalHeat, 1)} ${t("statistics.unit_gcal")}`}
        />
        <Stat
          label={t("statistics.balance_violations")}
          value={String(stats.balanceViolations)}
          tone={stats.balanceViolations > 0 ? "negative" : undefined}
        />
        <Stat
          label={t("statistics.alarm_hours")}
          value={String(stats.alarmHours)}
          tone={stats.alarmHours > 0 ? "negative" : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("statistics.chart_avg_power")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Line
                    type="monotone"
                    dataKey="avg_power_kw"
                    stroke="#2563eb"
                    dot={false}
                    name={t("statistics.avg_power")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("statistics.chart_profit_per_mwh")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Line
                    type="monotone"
                    dataKey="profit_per_mwh"
                    stroke="#10b981"
                    dot={false}
                    name={t("statistics.profit_per_mwh")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "negative";
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div
          className={
            "text-xl font-mono font-semibold " + (tone === "negative" ? "text-red-700" : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
